import { apiRequest } from './axiosConfig';

// Types for organization banners
export interface OrganizationBanner {
  id: number;
  organization_id: number;
  title: string | null;
  file_path: string;
  file_name: string;
  mime_type: string;
  file_size: number;
  sort_order: number;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  url: string; // presigned URL
}

export interface BannersResponse {
  data: OrganizationBanner[];
}

export interface BannerResponse {
  data: OrganizationBanner;
  message: string;
}

export interface BatchUploadResponse {
  data: OrganizationBanner[];
  errors: {
    index: number;
    file_name: string;
    error: string;
  }[];
  message: string;
}

class BannerService {
  /**
   * Get all banners for the current user's organization
   * @returns List of organization banners
   */
  async getBanners(): Promise<BannersResponse> {
    try {
      console.log('組織のバナーを取得中...');

      const config = {
        timeout: 30000,
      };

      const response = await apiRequest.get<BannersResponse>(
        'organization-banners',
        config
      );

      console.log(`${response.data.length}件のバナーを取得しました`);
      return response;
    } catch (error: any) {
      console.error('バナー取得に失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Upload a single banner
   * @param file Image file to upload
   * @param title Optional title for the banner
   * @returns Uploaded banner
   */
  async uploadBanner(file: File, title?: string): Promise<BannerResponse> {
    try {
      console.log('バナーをアップロード中...', file.name);

      const formData = new FormData();
      formData.append('image', file);
      if (title) {
        formData.append('title', title);
      }

      const config = {
        timeout: 60000,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      const response = await apiRequest.post<BannerResponse>(
        'organization-banners',
        formData,
        config
      );

      console.log('バナーのアップロードが完了しました');
      return response;
    } catch (error: any) {
      console.error('バナーアップロードに失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Upload multiple banners at once
   * @param files Array of image files to upload
   * @returns Uploaded banners and any errors
   */
  async uploadBannersBatch(files: File[]): Promise<BatchUploadResponse> {
    try {
      console.log(`${files.length}件のバナーをアップロード中...`);

      const formData = new FormData();
      files.forEach((file) => {
        formData.append('images[]', file);
      });

      const config = {
        timeout: 120000, // 2 minutes for batch upload
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      const response = await apiRequest.post<BatchUploadResponse>(
        'organization-banners/batch',
        formData,
        config
      );

      console.log(`${response.data.length}件のバナーをアップロードしました`);
      if (response.errors.length > 0) {
        console.warn(`${response.errors.length}件のバナーでエラーが発生しました`);
      }

      return response;
    } catch (error: any) {
      // Handle 422 response which may contain partial success data
      if (error?.response?.status === 422 && error?.response?.data) {
        const responseData = error.response.data as BatchUploadResponse;
        console.warn('一部のバナーの保存に失敗しました:', responseData.errors?.length || 0, '件のエラー');
        return {
          data: responseData.data || [],
          errors: responseData.errors || [],
          message: responseData.message || 'バナーの保存に失敗しました',
        };
      }
      console.error('バナーのバッチアップロードに失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Update a banner's title
   * @param bannerId Banner ID
   * @param title New title
   * @returns Updated banner
   */
  async updateBanner(bannerId: number, title: string | null): Promise<BannerResponse> {
    try {
      console.log(`バナー(ID: ${bannerId})を更新中...`);

      const config = {
        timeout: 30000,
      };

      const response = await apiRequest.put<BannerResponse>(
        `organization-banners/${bannerId}`,
        { title },
        config
      );

      console.log('バナーの更新が完了しました');
      return response;
    } catch (error: any) {
      console.error('バナー更新に失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Delete a banner
   * @param bannerId Banner ID
   * @returns Success message
   */
  async deleteBanner(bannerId: number): Promise<{ message: string }> {
    try {
      console.log(`バナー(ID: ${bannerId})を削除中...`);

      const config = {
        timeout: 30000,
      };

      const response = await apiRequest.delete<{ message: string }>(
        `organization-banners/${bannerId}`,
        config
      );

      console.log('バナーの削除が完了しました');
      return response;
    } catch (error: any) {
      console.error('バナー削除に失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }

  /**
   * Reorder banners
   * @param bannerIds Array of banner IDs in new order
   * @returns Updated banners
   */
  async reorderBanners(bannerIds: number[]): Promise<BannersResponse> {
    try {
      console.log('バナーを並び替え中...');

      const config = {
        timeout: 30000,
      };

      const response = await apiRequest.post<BannersResponse>(
        'organization-banners/reorder',
        { banner_ids: bannerIds },
        config
      );

      console.log('バナーの並び替えが完了しました');
      return response;
    } catch (error: any) {
      console.error('バナーの並び替えに失敗しました:', error?.message || 'エラー詳細不明');
      throw error;
    }
  }
}

export default new BannerService();
